<template>
  <g
    @mousemove="handleBoxHover"
    @mouseover="handleBoxHover"
    @click="handleBoxClick"
    :class="{
      'clickable-node': data.name !== currentNode?.name || isZoomed
    }"
  >
    <rect
      :width="width"
      :height="height"
      :fill="colors.boxColor"
    />
    <image v-if="isZoomed" height="14" y="4" x="6" fill="#FFFFFF" :href="ARROW_LEFT_ICON"></image>
    <text>
      <tspan font-weight="900" :dx="isZoomed ? 23 : 6" y="16" :style="{ fontFamily: Font.ROBOTO_CONDENSED}" :fill="colors.fontColor">{{ data.name }}</tspan>
    </text>
  </g>
</template>

<script lang="ts" setup>
  import { computed, toRefs } from 'vue';
  import { Font } from '@/ts/constants/fonts';
  import { isDarkColor } from '@/ts/util/colors';
  import { useMarketCarpetContext } from '@/ts/marketCarpetContext';
  import type { ParentNode } from '@/ts/composables/useMarketCarpetData';
  import { isLeafBoxHoverData } from '@/ts/composables/useHoverBox';
  import ARROW_LEFT_ICON from '@/svg/arrow-left-solid.svg';

  const props = defineProps<{
    width: number;
    height: number;
    data: ParentNode;
    depth: number;
  }>();

  const { width, height, data, depth } = toRefs(props);
  const { colorScheme, getParentColor, hoverData, setParentBoxHoverData, isZoomed, currentNode, zoomIn, zoomOut } = useMarketCarpetContext();

  function handleBoxClick() {
    if (isZoomed.value) {
      zoomOut();
    } else {
      zoomIntoNode();
    }
  }

  function zoomIntoNode() {
    if (data.value.name === currentNode.value?.name)
      return;

    zoomIn(data.value);
  }

  const isRootNode = computed(() => {
    return currentNode.value?.name === data.value.name;
  });

  const colors = computed(() => {
    const isHoverData = hoverData.value?.name === data.value.name;
    const isHoverDataParent = isLeafBoxHoverData(hoverData.value) && hoverData.value.parent?.data.name === data.value.name;

    // check if the box (or a child of the box) is hovered, and if it is not the root node. Set the color to the last color in the color scheme instead
    if ( (isHoverData || isHoverDataParent) && !isRootNode.value) {
      const boxColor = colorScheme.value[colorScheme.value.length - 1];
      const fontColor = isDarkColor(boxColor) ? '#FFFFFF' : '#000000';
      return { boxColor, fontColor };
    }

    return getParentColor(depth.value);
  });

  function handleBoxHover(mouseEvent: MouseEvent) {
    setParentBoxHoverData(data.value.name, mouseEvent);
  }
</script>

<style scoped>
  text {
    user-select: none;
  }

  .clickable-node {
    cursor: pointer;
  }
</style>
